import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Mail,
  MapPin,
  GraduationCap,
  Briefcase,
  Compass,
  Quote,
  Sparkles,
} from 'lucide-react';
import PortalLayout from '../components/portal/PortalLayout';

const NEIL_PHOTO =
  'https://customer-assets-4nw71qhi.emergentagent.net/job_analytics-live-1/artifacts/8inhc1di_neil_photo.webp';

const FOUNDER_EMAIL = 'nalvanzo@gmail.com';
const MAILTO =
  `mailto:${FOUNDER_EMAIL}` +
  `?subject=${encodeURIComponent('Coaching inquiry — Neil Alvanzo')}` +
  `&body=${encodeURIComponent(
    'Hi Neil,\n\nI came from The Reset portal and would love to learn more about your coaching.\n\nThanks,\n',
  )}`;

const PILLARS = [
  {
    title: 'Purpose',
    body:
      'Move past self-limiting stories and reconnect to the work you were built for.',
  },
  {
    title: 'Leadership',
    body:
      'Lead from a settled centre — at home, at work, in every room you walk into.',
  },
  {
    title: 'Legacy',
    body:
      'Design the life your children will one day describe. Live it on purpose.',
  },
];

const CREDS = [
  {
    icon: GraduationCap,
    label: 'Education',
    lines: ['BS · Howard University', 'MBA · Johns Hopkins Carey Business School'],
  },
  {
    icon: Briefcase,
    label: 'Career',
    lines: ['Salesforce · Booz Allen Hamilton · IBM', 'Leadership Development Program alum'],
  },
  {
    icon: Sparkles,
    label: 'Certification',
    lines: ['ICF-accredited iPEC coach', 'Energy Leadership Assessment practitioner'],
  },
  {
    icon: MapPin,
    label: 'Base',
    lines: ['Baltimore, MD', 'Coaches clients across North America'],
  },
];

const TESTIMONIALS = [
  {
    quote:
      'Neil doesn\u2019t give you a pep talk. He hands you a mirror and sits with you while you look. That\u2019s rare.',
    name: 'Marcus',
    role: 'Reset 2026 attendee',
  },
  {
    quote:
      'I came in trying to fix my career. I left with a plan for my marriage, my body, and my next twenty years. All of it moved.',
    name: 'DeAndre',
    role: 'Client since 2025',
  },
  {
    quote:
      'For the first time in a long time, I was in a room with brothers where I didn\u2019t have to perform. Neil built that.',
    name: 'Kwame',
    role: 'Brotherhood Circle member',
  },
];

export default function WelcomeNeil() {
  return (
    <PortalLayout>
      <div className="max-w-5xl mx-auto px-5 py-12" data-testid="welcome-neil-page">
        {/* Back link */}
        <Link
          to="/portal"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-[#D4AF37] font-manrope text-sm transition-colors mb-8"
          data-testid="welcome-neil-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to the portal
        </Link>

        {/* Hero */}
        <section
          className="grid md:grid-cols-5 gap-8 items-center mb-16"
          data-testid="welcome-neil-hero"
        >
          <div className="md:col-span-2 order-2 md:order-1">
            <div className="relative">
              {/* Ambient gold glow */}
              <div className="absolute -inset-6 bg-[#D4AF37]/20 blur-3xl rounded-full pointer-events-none" />
              {/* Emblem watermark bottom-left */}
              <img
                src="https://customer-assets-4nw71qhi.emergentagent.net/job_analytics-live-1/artifacts/8w3x6659_brotherhood-reset-logo.jpg"
                alt=""
                aria-hidden="true"
                className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full object-cover border-2 border-[#0A0A0A] shadow-xl shadow-black/50 z-10"
                data-testid="welcome-neil-emblem"
              />
              {/* Founder chip top-left */}
              <div className="absolute top-4 left-4 bg-[#0A0A0A]/85 backdrop-blur-sm border border-[#D4AF37]/40 text-[#D4AF37] font-manrope text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1.5 rounded-full z-10">
                The Founder
              </div>
              <img
                src={NEIL_PHOTO}
                alt="Neil Alvanzo, founder of The Reset"
                className="relative w-full max-w-xs md:max-w-none rounded-2xl object-cover border border-[#D4AF37]/50 shadow-2xl shadow-black/60"
                data-testid="welcome-neil-photo"
              />
            </div>
          </div>

          <div className="md:col-span-3 order-1 md:order-2">
            <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.3em] text-[11px] font-bold mb-4">
              The Founder &middot; The Reset
            </p>
            <h1 className="font-cormorant text-5xl md:text-6xl font-medium text-white leading-[1.05] mb-5">
              Meet <span className="italic text-[#D4AF37]">Neil Alvanzo.</span>
            </h1>
            <p className="text-zinc-300 font-manrope text-lg leading-relaxed mb-6 max-w-xl">
              Coach. Executive. Husband. Father. Founder of a movement built for
              Black men who are ready to trade survival mode for a life they actually
              designed.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <a
                href={MAILTO}
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] px-6 py-3 rounded-full font-manrope font-semibold hover:bg-amber-200 transition-colors"
                data-testid="welcome-neil-email-btn"
              >
                <Mail className="w-4 h-4" /> Email Neil directly
              </a>
              <Link
                to="/portal/coaching"
                className="inline-flex items-center gap-2 border border-white/15 text-white px-6 py-3 rounded-full font-manrope font-medium hover:border-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors"
                data-testid="welcome-neil-coaching-link"
              >
                See the coaching lineup <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Story */}
        <section
          className="rounded-2xl bg-gradient-to-br from-[#D4AF37]/12 via-[#111111] to-[#111111] border border-[#D4AF37]/25 p-8 md:p-12 mb-14"
          data-testid="welcome-neil-story"
        >
          <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.3em] text-[11px] font-bold mb-4">
            The Story
          </p>
          <div className="space-y-5 text-zinc-300 font-manrope text-base md:text-lg leading-relaxed">
            <p>
              Neil spent twenty years leading teams inside some of the most demanding
              companies in the world &mdash;{' '}
              <span className="text-white">Salesforce, Booz Allen Hamilton, and IBM.</span>{' '}
              From the outside, everything was working. From the inside, he could feel
              what a lot of Black men quietly feel: successful on paper, and still
              carrying the weight alone.
            </p>
            <p>
              That tension is what became The Reset. A place, first for himself and
              then for other brothers, to stop performing and start rebuilding. To
              breathe. To be honest. To design something better than the default.
            </p>
            <p>
              Today Neil is an ICF-accredited iPEC coach and a certified practitioner
              of the Energy Leadership Assessment. He partners with Black men and
              executives across North America who are ready to lead their own lives
              &mdash; not just their calendars.
            </p>
            <p className="font-cormorant text-2xl md:text-3xl italic text-white leading-snug pt-2 border-t border-white/10">
              The Reset isn&rsquo;t about escaping. It&rsquo;s about coming home to
              yourself &mdash; and staying.
            </p>
          </div>
        </section>

        {/* What Neil works on with brothers */}
        <section className="mb-14" data-testid="welcome-neil-pillars">
          <div className="flex items-baseline justify-between mb-5">
            <h2 className="font-cormorant text-3xl md:text-4xl font-medium text-white">
              What we work on together
            </h2>
            <p className="text-zinc-500 font-manrope text-xs uppercase tracking-widest">
              Three rooms
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {PILLARS.map((p) => (
              <div
                key={p.title}
                className="rounded-2xl bg-[#111111] border border-white/10 p-6"
                data-testid={`welcome-neil-pillar-${p.title.toLowerCase()}`}
              >
                <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center mb-4">
                  <Compass className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="font-cormorant text-2xl font-medium text-white mb-2">
                  {p.title}
                </h3>
                <p className="text-zinc-400 font-manrope text-sm leading-relaxed">
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials from brothers */}
        <section className="mb-14" data-testid="welcome-neil-testimonials">
          <div className="flex items-baseline justify-between mb-5">
            <h2 className="font-cormorant text-3xl md:text-4xl font-medium text-white">
              What brothers say
            </h2>
            <p className="text-zinc-500 font-manrope text-xs uppercase tracking-widest">
              In their own words
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {TESTIMONIALS.map((t) => (
              <figure
                key={t.name}
                className="rounded-2xl bg-[#111111] border border-white/10 p-6 flex flex-col relative overflow-hidden"
                data-testid={`welcome-neil-testimonial-${t.name.toLowerCase()}`}
              >
                <Quote className="w-5 h-5 text-[#D4AF37] mb-3" />
                <blockquote className="font-cormorant text-xl italic text-white leading-snug mb-5 flex-1">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="border-t border-white/5 pt-3">
                  <p className="text-white font-manrope text-sm font-semibold">
                    {t.name}
                  </p>
                  <p className="text-zinc-500 font-manrope text-xs uppercase tracking-widest mt-0.5">
                    {t.role}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* Credentials strip */}
        <section
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14"
          data-testid="welcome-neil-creds"
        >
          {CREDS.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.label}
                className="rounded-2xl bg-[#111111] border border-white/10 p-5"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="w-4 h-4 text-[#D4AF37]" />
                  <p className="text-[#D4AF37] font-manrope text-[10px] uppercase tracking-widest font-bold">
                    {c.label}
                  </p>
                </div>
                {c.lines.map((line) => (
                  <p
                    key={line}
                    className="text-zinc-300 font-manrope text-sm leading-relaxed"
                  >
                    {line}
                  </p>
                ))}
              </div>
            );
          })}
        </section>

        {/* Closing quote */}
        <section
          className="rounded-2xl bg-[#111111] border border-[#D4AF37]/25 p-8 md:p-12 relative overflow-hidden"
          data-testid="welcome-neil-closing"
        >
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#D4AF37]/10 blur-3xl pointer-events-none" />
          <Quote className="w-6 h-6 text-[#D4AF37] mb-4" />
          <p className="font-cormorant text-2xl md:text-3xl italic text-white leading-snug mb-4 max-w-3xl">
            &ldquo;Every man deserves a place where he can be honest, be
            challenged, and be seen. That&rsquo;s what we&rsquo;re building
            here.&rdquo;
          </p>
          <p className="text-zinc-500 font-manrope text-xs uppercase tracking-widest">
            &mdash; Neil Alvanzo
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href={MAILTO}
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] px-6 py-3 rounded-full font-manrope font-semibold hover:bg-amber-200 transition-colors"
              data-testid="welcome-neil-cta"
            >
              <Mail className="w-4 h-4" /> Reach out to Neil
            </a>
            <Link
              to="/portal/monthly-meeting"
              className="inline-flex items-center gap-2 text-[#D4AF37] font-manrope text-sm hover:text-amber-200 transition-colors"
            >
              Join the next Brotherhood Circle <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </PortalLayout>
  );
}
